import axios, { AxiosRequestConfig } from 'axios';
import { message } from 'ant-design-vue';
import { ERROR_CODE_MESSAGE } from './config';

import { getToken, TOKEN_INVALID_ERRCODE } from '@/utils';

import { UserService } from '@/services/user.service';
import { useService } from '@kaokei/use-vue-service';

function onLogoutError() {
  const userService = useService(UserService, undefined, true);
  userService && userService.logout();
}

type AxiosRequestConfigExtend = AxiosRequestConfig & {
  X_DISABLE_ERROR?: boolean;
  X_DISABLE_UNWRAP?: boolean;
};

type AxiosRequestConfigExtendWithResponse = AxiosRequestConfig & {
  X_DISABLE_ERROR?: boolean;
  X_DISABLE_UNWRAP: true;
};

type AxiosRequestConfigExtendWithoutResponse = AxiosRequestConfig & {
  X_DISABLE_ERROR?: boolean;
  X_DISABLE_UNWRAP?: false;
};

interface Response<T> {
  errCode: number;
  errMsg: string;
  success: boolean;
  data: T;
}

const instance = axios.create({
  // timeout: 1000,
  timeout: 50000,
  withCredentials: false,
});

// 添加请求拦截器
instance.interceptors.request.use(
  function (config) {
    // 在发送请求之前做些什么
    const token = getToken() || '';
    if (token) {
      config.headers = config.headers || {};
      config.headers['token'] = token;
    }
    return config;
  },
  function (error) {
    // 对请求错误做些什么
    if (!(error && error.config && error.config.X_DISABLE_ERROR)) {
      message.error('请求出错了，请稍后刷新页面重试！');
    }
    return Promise.reject(error);
  }
);

// 添加响应拦截器
instance.interceptors.response.use(
  function (response) {
    // 对响应数据做点什么
    const responseData = response.data;
    const responseConfig = response.config;
    const { X_DISABLE_ERROR, X_DISABLE_UNWRAP } =
      responseConfig as AxiosRequestConfigExtend;
    const { errCode, errMsg, errcode, errmsg } = responseData;
    const errCode2 = errCode === undefined ? errcode : errCode;
    const errMsg2 = errMsg === undefined ? errmsg : errMsg;

    if (errCode2 === 0) {
      // 这里有点trick
      // 这里看起来返回的是response和responseData
      // 但是在各个请求方法的then函数中存在这个逻辑`res => res.data`
      // 所以最终业务中使用的数据是response.data和responseData.data
      return X_DISABLE_UNWRAP ? response : responseData;
    } else {
      if (!X_DISABLE_ERROR) {
        if (errCode2 === TOKEN_INVALID_ERRCODE) {
          // 统一处理所有接口返回10000的错误码
          onLogoutError();
        }
        message.error(
          ERROR_CODE_MESSAGE[errCode2] ||
            errMsg2 ||
            '请求出错了，请稍后刷新页面重试！'
        );
      }

      return Promise.reject(responseData);
    }
  },
  function (error) {
    // 对响应错误做点什么
    if (!(error && error.config && error.config.X_DISABLE_ERROR)) {
      const errMsg = navigator.onLine
        ? '请求出错了，请稍后刷新页面重试！'
        : '网络异常';
      message.error(errMsg);
    }
    return Promise.reject(error);
  }
);

function request<T>(
  config: AxiosRequestConfigExtendWithResponse
): Promise<Response<T>>;
function request<T>(
  config: AxiosRequestConfigExtendWithoutResponse
): Promise<T>;
function request<T>(config: AxiosRequestConfigExtend) {
  return instance.request<Response<T>>(config).then(res => res.data);
}

function get<T>(
  url: string,
  config: AxiosRequestConfigExtendWithResponse
): Promise<Response<T>>;
function get<T>(
  url: string,
  config?: AxiosRequestConfigExtendWithoutResponse
): Promise<T>;
function get<T>(url: string, config?: AxiosRequestConfigExtend) {
  return instance.get<Response<T>>(url, config).then(res => res.data);
}

function myDelete<T>(
  url: string,
  config: AxiosRequestConfigExtendWithResponse
): Promise<Response<T>>;
function myDelete<T>(
  url: string,
  config?: AxiosRequestConfigExtendWithoutResponse
): Promise<T>;
function myDelete<T>(url: string, config?: AxiosRequestConfigExtend) {
  return instance.delete<Response<T>>(url, config).then(res => res.data);
}

function head<T>(
  url: string,
  config: AxiosRequestConfigExtendWithResponse
): Promise<Response<T>>;
function head<T>(
  url: string,
  config?: AxiosRequestConfigExtendWithoutResponse
): Promise<T>;
function head<T>(url: string, config?: AxiosRequestConfigExtend) {
  return instance.head<Response<T>>(url, config).then(res => res.data);
}

function options<T>(
  url: string,
  config: AxiosRequestConfigExtendWithResponse
): Promise<Response<T>>;
function options<T>(
  url: string,
  config?: AxiosRequestConfigExtendWithoutResponse
): Promise<T>;
function options<T>(url: string, config?: AxiosRequestConfigExtend) {
  return instance.options<Response<T>>(url, config).then(res => res.data);
}

function post<T>(
  url: string,
  data: SafeAny,
  config: AxiosRequestConfigExtendWithResponse
): Promise<Response<T>>;
function post<T>(
  url: string,
  data?: SafeAny,
  config?: AxiosRequestConfigExtendWithoutResponse
): Promise<T>;
function post<T>(
  url: string,
  data?: SafeAny,
  config?: AxiosRequestConfigExtend
) {
  return instance.post<Response<T>>(url, data, config).then(res => res.data);
}

function put<T>(
  url: string,
  data: SafeAny,
  config: AxiosRequestConfigExtendWithResponse
): Promise<Response<T>>;
function put<T>(
  url: string,
  data?: SafeAny,
  config?: AxiosRequestConfigExtendWithoutResponse
): Promise<T>;
function put<T>(
  url: string,
  data?: SafeAny,
  config?: AxiosRequestConfigExtend
) {
  return instance.put<Response<T>>(url, data, config).then(res => res.data);
}

function patch<T>(
  url: string,
  data: SafeAny,
  config: AxiosRequestConfigExtendWithResponse
): Promise<Response<T>>;
function patch<T>(
  url: string,
  data?: SafeAny,
  config?: AxiosRequestConfigExtendWithoutResponse
): Promise<T>;
function patch<T>(
  url: string,
  data?: SafeAny,
  config?: AxiosRequestConfigExtend
) {
  return instance.patch<Response<T>>(url, data, config).then(res => res.data);
}

export default {
  request,
  get,
  delete: myDelete,
  head,
  options,
  post,
  put,
  patch,
};
